import ContextPill from '../ui/ContextPill'
import { concentrationLabel } from '../../lib/homeNav'
import { untieredNotes } from '../../lib/untieredNotes'
import { vibeTagLabel } from '../../lib/vibeVocabulary'

const uniqueText = values => [...new Set(values
  .flatMap(value => Array.isArray(value) ? value : [value])
  .map(value => String(value || '').trim())
  .filter(Boolean))]

function ShareIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3v12" /><path d="M8 7l4-4 4 4" /><path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" />
    </svg>
  )
}

export function primaryWearContextLabel(selectionSource) {
  return selectionSource === 'gym' ? 'Your gym scent' : 'Scent of the day'
}

export function primaryWearNotes(fragrance = {}) {
  const groups = [
    ['Top', fragrance.top_notes],
    ['Middle', fragrance.mid_notes],
    ['Base', fragrance.base_notes],
  ].map(([label, notes]) => ({ label, notes: uniqueText(notes || []) }))
    .filter(group => group.notes.length > 0)

  if (groups.length > 0) return groups
  const notes = untieredNotes(fragrance)
  return notes.length > 0 ? [{ label: '', notes }] : []
}

export function primaryWearVibes(fragrance = {}) {
  const raw = Array.isArray(fragrance.vibe_tags)
    ? fragrance.vibe_tags
    : typeof fragrance.vibe_tags === 'string'
      ? (() => { try { return JSON.parse(fragrance.vibe_tags) } catch { return [] } })()
      : []
  return uniqueText(raw.map(entry => {
    const tag = typeof entry === 'string' ? entry : entry?.tag
    return vibeTagLabel(tag) || ''
  }))
}

export default function PrimaryWearCard({ fragrance, log, description, onOpenLayers, onOpenFragrance, onShare, primaryWearModelEnabled = false }) {
  const notes = primaryWearNotes(fragrance)
  const situations = uniqueText([
    fragrance?.occasions || [],
    log?.occasion,
    (log?.context_tags || []).map(item => item?.display_name || item?.context_tag || item?.tag),
  ])
  const vibes = primaryWearVibes(fragrance)
  const layers = uniqueText(log?.layered_with || [])
  const concentration = concentrationLabel(fragrance)
  const wearNote = String(log?.note || log?.notes || '').trim()

  return (
    <section
      aria-labelledby="primary-wear-name"
      className="relative min-h-11 min-w-0 overflow-hidden rounded-2xl border border-gold/70 bg-gradient-to-br from-charcoal-700 to-charcoal-800 p-4"
      data-sotd-hero
      data-primary-wear-card
      data-hero-fragrance-id={fragrance.id}
      data-wear-log-id={log.id}
    >
      <header className="flex min-w-0 items-start justify-between gap-3">
        <p className="min-w-0 pt-3 text-xs font-semibold tracking-wide text-gold" data-primary-wear-label>
          {primaryWearContextLabel(log?.wear_label)}
        </p>
        <div className="shrink-0" data-share-slot>
          <button
            type="button"
            disabled={!onShare}
            onClick={onShare}
            aria-label={onShare ? `Share ${fragrance.name}` : `Share ${fragrance.name} (coming soon)`}
            title={onShare ? undefined : 'Sharing is coming soon'}
            className={`flex min-h-11 min-w-11 items-center justify-center rounded-full ${onShare ? 'text-gold' : 'text-gold/40 disabled:cursor-not-allowed'}`}
          >
            <ShareIcon />
          </button>
        </div>
      </header>

      <h2 id="primary-wear-name" className="mt-2 break-words font-body text-2xl leading-tight text-cream">
        <button type="button" onClick={onOpenFragrance} aria-label={`Open the fragrance card for ${fragrance.name}`} className="flex min-h-11 w-full items-center text-left">
          {fragrance.name}
        </button>
      </h2>
      {concentration && <p className="mt-1 text-sm text-cream/50" data-primary-wear-concentration>{concentration}</p>}

      {primaryWearModelEnabled && layers.length > 0 && (
        <div data-primary-fragrances className="mt-3 flex min-w-0 flex-wrap items-center gap-2" aria-label="Primary fragrances">
          {[fragrance.name, ...layers].map((name, index) => (
            <span key={`${name}:${index}`} className="pill pill-gold max-w-full break-words text-xs">{name}</span>
          ))}
        </div>
      )}

      {description && (
        <p className="mt-4 max-w-prose text-sm leading-6 text-cream/75" data-primary-wear-description>
          {description}
        </p>
      )}

      <section className="mt-5 border-t border-white/10 pt-4" aria-labelledby="primary-wear-notes-title">
        <h3 id="primary-wear-notes-title" className="text-sm font-semibold text-cream/80">Notes</h3>
        {notes.length > 0 ? (
          <div className="mt-2 space-y-2.5">
            {notes.map(group => (
              <div key={group.label || 'notes'} className="flex min-w-0 items-start gap-2">
                {group.label && <span className="w-12 shrink-0 pt-1 text-[11px] text-cream/40">{group.label}</span>}
                <div className="flex min-w-0 flex-wrap gap-1.5">
                  {group.notes.slice(0, 8).map(note => <span key={note} className="pill pill-note max-w-full break-words text-xs">{note}</span>)}
                </div>
              </div>
            ))}
          </div>
        ) : <p className="mt-1 text-xs text-cream/35">No notes stored yet.</p>}
        {wearNote && <p className="mt-3 text-xs leading-5 text-cream/60"><span className="text-cream/40">Wear note:</span> {wearNote}</p>}
      </section>

      <div className="mt-5 grid min-w-0 grid-cols-2 gap-4 border-t border-white/10 pt-4">
        <section className="min-w-0" aria-labelledby="primary-wear-situations-title">
          <h3 id="primary-wear-situations-title" className="text-sm font-semibold text-cream/80">Situations</h3>
          {situations.length > 0 ? (
            <div className="mt-2 flex min-w-0 flex-wrap gap-1.5">
              {situations.map(situation => <ContextPill key={situation} tag={situation} className="pill max-w-full break-words border border-white/10 px-2 py-1 text-xs text-cream/60" />)}
            </div>
          ) : <p className="mt-1 text-xs text-cream/35">None stored yet.</p>}
        </section>
        <section className="min-w-0" aria-labelledby="primary-wear-vibes-title">
          <h3 id="primary-wear-vibes-title" className="text-sm font-semibold text-cream/80">Vibes</h3>
          {vibes.length > 0 ? (
            <div className="mt-2 flex min-w-0 flex-wrap gap-1.5">
              {vibes.map(vibe => <span key={vibe} className="pill pill-gold max-w-full break-words text-xs">{vibe}</span>)}
            </div>
          ) : <p className="mt-1 text-xs text-cream/35">None stored yet.</p>}
        </section>
      </div>

      <button
        type="button"
        disabled
        data-note-button
        title="Adding a note is coming soon"
        className="mt-5 flex min-h-11 w-full items-center justify-center rounded-xl border border-white/10 px-4 text-sm font-semibold text-cream/40 disabled:cursor-not-allowed"
      >
        Note
      </button>

      <section className="mt-4 border-t border-white/10 pt-4" aria-labelledby="primary-wear-layering-title">
        <div className="flex min-w-0 items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 id="primary-wear-layering-title" className="text-sm font-semibold text-cream/80">Layering</h3>
            {layers.length > 0 && <p className="mt-1 break-words text-xs leading-5 text-gold/80">{layers.join(' + ')}</p>}
          </div>
          <button
            type="button"
            onClick={onOpenLayers}
            className="flex min-h-11 shrink-0 items-center justify-center rounded-xl border border-gold/35 px-4 text-sm font-semibold text-gold"
          >
            {layers.length > 0 ? 'Edit layers' : 'Add a layer'}
          </button>
        </div>
      </section>
    </section>
  )
}
